import * as React from "react"
import { Tooltip as TooltipPrimitive } from "radix-ui"

import { cn } from "~/lib/utils"

/**
 * The kit's tooltip (kit .tooltip): 12 medium on the tooltip ground, an 8
 * corner, after a short wait, with a notch that points at what is hovered: an
 * 8 square turned 45 degrees and sunk 3 into the body (kit.css .tooltip::after),
 * 10 from the trigger to the body (web-shell.css .rail-tip-in). It opens on the
 * side asked for and turns to the other side when that one has no room
 * (Radix's collision handling, which also moves the notch). It names an
 * icon-only button with the same words a screen reader hears.
 *
 * It grows from its trigger (Radix's transform origin) at `enter` on the `out`
 * curve and leaves at `leave`. Once one tooltip has shown, the next one within
 * the skip delay opens at once and without animating (Radix marks it
 * `instant-open`): moving along a row of icons reads each name without a wait.
 */
function TooltipProvider({
  delayDuration = 300,
  skipDelayDuration = 300,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      {...props}
    />
  )
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipContent({
  className,
  // Radix adds the notch's own 8 to this, so the body sits 10 from the trigger.
  sideOffset = 2,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "z-50 inline-flex w-fit origin-(--radix-tooltip-content-transform-origin) items-center gap-1.5 rounded-small bg-tooltip px-2 py-1 text-xs font-medium whitespace-nowrap text-tooltip-foreground",
          "animate-in fade-in-0 zoom-in-95 duration-(--duration-enter) ease-out data-[state=instant-open]:animate-none data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:duration-(--duration-leave)",
          className
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow
          width={8}
          height={8}
          className="block size-2 translate-y-[calc(-50%_-_1px)] rotate-45 rounded-[1px] bg-tooltip fill-tooltip"
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
